import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDateTime, type Locale } from '@barev/i18n';
import { navigate, useToast, useTRPC } from '@barev/web';
import { useEffect, useMemo, useState } from 'react';
import { errText, useT } from '../i18n.tsx';
import { Avatar, Dialog, Field, I, PlanBadge } from '../components/kit.tsx';

export type ParamField =
  | { key: string; type: 'select'; options: string[]; default?: string }
  | { key: string; type: 'int'; min: number; max: number; default?: number }
  | { key: string; type: 'text'; maxLength: number; required?: boolean }
  | { key: string; type: 'bool'; default?: boolean }
  | { key: string; type: 'extras'; items: { code: string; max: number }[] };
type Params = Record<string, unknown>;

/** Дата по Еревану в виде YYYY-MM-DD */
const dayKey = (d: Date) => new Date(d.getTime() + 4 * 3600_000).toISOString().slice(0, 10);
/** Время по Еревану из даты и «чч:мм» */
const atYerevan = (key: string, hhmm: string) => new Date(`${key}T${hhmm}:00+04:00`);

function defaults(fields: ParamField[]): Params {
  const out: Params = {};
  for (const f of fields) {
    if (f.type === 'select') out[f.key] = f.default ?? f.options[0];
    else if (f.type === 'int') out[f.key] = f.default ?? f.min;
    else if (f.type === 'bool') out[f.key] = f.default ?? false;
    else if (f.type === 'extras') out[f.key] = {};
  }
  return out;
}

/**
 * Время заказа: свободные слоты на выбранный день (с учётом графиков и уже назначенных заказов)
 * или «другое время» — оператор договорился сам.
 */
export function SlotPicker({ service, params, excludeOrderId, initial, onChange }: { service: string; params: Params; excludeOrderId?: string; initial?: Date; onChange: (v: { when: Date; busy: boolean } | null) => void }) {
  const { t, time, locale } = useT();
  const trpc = useTRPC();
  const [day, setDay] = useState(dayKey(initial && initial > new Date() ? initial : new Date()));
  const [picked, setPicked] = useState<Date | null>(null);
  const [custom, setCustom] = useState(false);
  const [hhmm, setHhmm] = useState('10:00');
  const slots = useQuery({ ...trpc.bo.orders.slots.queryOptions({ service, params, dateKey: day, excludeOrderId }), placeholderData: keepPreviousData });
  useEffect(() => {
    if (custom) onChange(hhmm ? { when: atYerevan(day, hhmm), busy: true } : null);
    else onChange(picked ? { when: picked, busy: false } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custom, hhmm, day, picked]);
  const days = useMemo(() => {
    const out: string[] = [];
    for (let i = 0; i < 14; i++) out.push(dayKey(new Date(Date.now() + i * 86_400_000)));
    return out;
  }, []);
  const free = (slots.data ?? []).filter((s) => s.capacity > 0);

  return (
    <div className="bo-col" style={{ gap: 12 }}>
      <div className="bo-days" role="group" aria-label={t('no.day')}>
        {days.map((k) => {
          const d = atYerevan(k, '12:00');
          return (
            <button
              key={k}
              type="button"
              className="bo-day"
              aria-pressed={k === day}
              onClick={() => {
                setDay(k);
                setPicked(null);
              }}
            >
              <span>{formatDateTime(locale as Locale, d, { weekday: 'short' })}</span>
              <b>{formatDateTime(locale as Locale, d, { day: 'numeric' })}</b>
            </button>
          );
        })}
      </div>
      {!custom ? (
        free.length === 0 ? (
          <p className="bo-hint">{slots.isFetching ? t('ui.loading') : t('no.noSlots')}</p>
        ) : (
          <div className="bo-slots">
            {free.map((s) => (
              <button key={s.start.toISOString()} type="button" className="bo-slot" aria-pressed={picked?.getTime() === s.start.getTime()} onClick={() => setPicked(s.start)}>
                {time(s.start)}
                <span>{s.capacity}</span>
              </button>
            ))}
          </div>
        )
      ) : (
        <Field label={t('no.customTime')} hint={t('no.customHint')}>
          <input className="bo-input" type="time" value={hhmm} onChange={(e) => setHhmm(e.target.value)} step={900} />
        </Field>
      )}
      <label className="bo-check">
        <input type="checkbox" checked={custom} onChange={(e) => setCustom(e.target.checked)} />
        {t('no.otherTime')}
      </label>
    </div>
  );
}

/** Новый заказ за клиента: услуга → параметры → адрес → время. Цена — с его скидкой по подписке */
export function NewOrderDialog({ clientId: fixedClient, onClose }: { clientId?: string; onClose: () => void }) {
  const { t, shared, amd } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const [clientId, setClientId] = useState(fixedClient ?? '');
  const services = useQuery(trpc.bo.orders.services.queryOptions());
  const dict = useQuery(trpc.bo.orders.dictionaries.queryOptions());
  const [svc, setSvc] = useState('');
  const service = services.data?.find((s) => s.code === svc) ?? services.data?.[0];
  const fields = (service?.paramsSchema.fields ?? []) as ParamField[];
  const [params, setParams] = useState<Params>({});
  useEffect(() => {
    if (service) setParams(defaults(fields));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.code]);
  const [address, setAddress] = useState('');
  const [districtId, setDistrictId] = useState('');
  const [note, setNote] = useState('');
  const [payment, setPayment] = useState('');
  const [at, setAt] = useState<{ when: Date; busy: boolean } | null>(null);
  const [promo, setPromo] = useState('');
  const [promoQ, setPromoQ] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setPromoQ(promo.trim()), 400);
    return () => clearTimeout(id);
  }, [promo]);
  const ready = !!service && Object.keys(params).length > 0;
  const quote = useQuery({ ...trpc.bo.orders.quote.queryOptions({ clientId, service: service?.code ?? '', params, promoCode: promoQ || undefined }), enabled: ready && !!clientId, placeholderData: keepPreviousData, retry: false });
  const create = useMutation(
    trpc.bo.orders.create.mutationOptions({
      onSuccess: (r) => {
        qc.invalidateQueries({ queryKey: trpc.bo.orders.list.queryKey() });
        qc.invalidateQueries({ queryKey: trpc.bo.clients.get.queryKey() });
        toast(t('no.created', { n: r.number }));
        onClose();
        navigate(`/orders/${r.id}`);
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  const needsAddress = service && !service.isOnline;
  const canSubmit = !!clientId && ready && !!at && (!needsAddress || (address.trim() && districtId)) && !create.isPending;

  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={t('no.title')}
      footer={
        <>
          {quote.data && (
            <span className="bo-grow bo-quote">
              <b className="bo-num">{amd(quote.data.total)}</b>
              <span className="bo-small bo-muted">
                {' '}
                · {t('ord.minutes', { n: quote.data.durationMinutes })}
                {quote.data.discountPct > 0 && ` · −${quote.data.discountPct}%`}
              </span>
            </span>
          )}
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button
            type="button"
            className="bo-btn bo-btn--accent"
            disabled={!canSubmit}
            onClick={() =>
              at &&
              service &&
              create.mutate({
                clientId,
                service: service.code,
                params,
                scheduledAt: at.when,
                allowBusySlot: at.busy,
                addressText: needsAddress ? address : undefined,
                districtId: needsAddress ? districtId : undefined,
                note: note || undefined,
                paymentMethodId: payment || undefined,
                promoCode: promoQ && !quote.isError ? promoQ : undefined,
              })
            }
          >
            {t('no.create')}
          </button>
        </>
      }
    >
      {!fixedClient && <ClientPicker value={clientId} onChange={setClientId} />}

      {(services.data?.length ?? 0) > 1 && (
        <Field label={t('no.service')}>
          <select className="bo-select" value={service?.code ?? ''} onChange={(e) => setSvc(e.target.value)}>
            {services.data!.map((s) => (
              <option key={s.code} value={s.code}>
                {shared(`services.${s.code}.name`)}
              </option>
            ))}
          </select>
        </Field>
      )}

      {service && <ParamsForm service={service.code} fields={fields} value={params} onChange={setParams} />}

      {needsAddress && (
        <div className="bo-row2">
          <Field label={t('no.address')}>
            <input className="bo-input" value={address} maxLength={300} onChange={(e) => setAddress(e.target.value)} placeholder={t('no.addressPh')} />
          </Field>
          <Field label={t('no.district')}>
            <select className="bo-select" value={districtId} onChange={(e) => setDistrictId(e.target.value)}>
              <option value="">—</option>
              {dict.data?.districts.map((d) => (
                <option key={d.id} value={d.id}>
                  {shared(`districts.${d.code}`)}
                </option>
              ))}
            </select>
          </Field>
        </div>
      )}

      {ready && (
        <div>
          <span className="bo-label">{t('no.when')}</span>
          <div style={{ marginTop: 6 }}>
            <SlotPicker service={service!.code} params={params} onChange={setAt} />
          </div>
        </div>
      )}

      <div className="bo-row2">
        <Field label={t('no.note')}>
          <input className="bo-input" value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} placeholder={t('no.notePh')} />
        </Field>
        <Field label={t('no.payment')}>
          <select className="bo-select" value={payment} onChange={(e) => setPayment(e.target.value)}>
            <option value="">—</option>
            {dict.data?.paymentMethods.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name.ru ?? m.code}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field
        label={t('no.promo')}
        hint={promoQ ? (quote.isError ? errText(t, quote.error) : quote.data?.promo ? t('no.promoOk', { code: quote.data.promo }) : quote.data ? t('no.promoWeaker') : undefined) : undefined}
      >
        <input className="bo-input" value={promo} maxLength={40} onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder={t('no.promoPh')} style={{ maxWidth: 260 }} />
      </Field>
      <p className="bo-hint">{t('no.hint')}</p>
    </Dialog>
  );
}

export function ParamsForm({ service, fields, value, onChange }: { service: string; fields: ParamField[]; value: Params; onChange: (v: Params) => void }) {
  const { t, shared } = useT();
  const label = (k: string) => {
    const key = `params.${service}.${k}.label`;
    const v = shared(key);
    return v === key ? k : v;
  };
  const opt = (k: string, o: string) => {
    const key = `params.${service}.${k}.${o}`;
    const v = shared(key);
    return v === key ? o : v;
  };
  const set = (k: string, v: unknown) => onChange({ ...value, [k]: v });
  const [showExtras, setShowExtras] = useState(false);
  return (
    <div className="bo-col" style={{ gap: 12 }}>
      <div className="bo-row2">
        {fields
          .filter((f) => f.type === 'select' || f.type === 'int')
          .map((f) =>
            f.type === 'select' ? (
              <Field key={f.key} label={label(f.key)}>
                <select className="bo-select" value={String(value[f.key] ?? '')} onChange={(e) => set(f.key, e.target.value)}>
                  {f.options.map((o) => (
                    <option key={o} value={o}>
                      {opt(f.key, o)}
                    </option>
                  ))}
                </select>
              </Field>
            ) : f.type === 'int' ? (
              <Field key={f.key} label={label(f.key)}>
                <Stepper value={Number(value[f.key] ?? f.min)} min={f.min} max={f.max} onChange={(n) => set(f.key, n)} />
              </Field>
            ) : null,
          )}
      </div>
      {fields.map((f) =>
        f.type === 'text' ? (
          <Field key={f.key} label={label(f.key)}>
            <textarea className="bo-textarea" style={{ minHeight: 70 }} maxLength={f.maxLength} value={String(value[f.key] ?? '')} onChange={(e) => set(f.key, e.target.value)} />
          </Field>
        ) : f.type === 'bool' ? (
          <label key={f.key} className="bo-check">
            <input type="checkbox" checked={!!value[f.key]} onChange={(e) => set(f.key, e.target.checked)} />
            {label(f.key)}
          </label>
        ) : f.type === 'extras' ? (
          <div key={f.key}>
            <button type="button" className="bo-side__link" style={{ color: 'var(--bc-accent)', padding: 0 }} onClick={() => setShowExtras((s) => !s)} aria-expanded={showExtras}>
              {t('no.extras')} {Object.values((value[f.key] as Record<string, number>) ?? {}).filter((n) => n > 0).length || ''} {showExtras ? '▴' : '▾'}
            </button>
            {showExtras && (
              <div className="bo-extras">
                {f.items.map((it) => {
                  const ex = (value[f.key] as Record<string, number>) ?? {};
                  const n = ex[it.code] ?? 0;
                  return (
                    <div key={it.code} className="bo-extra" data-on={n > 0 || undefined}>
                      <span className="bo-grow">{shared(`extras.${it.code}.name`)}</span>
                      {it.max === 1 ? (
                        <input type="checkbox" checked={n > 0} onChange={(e) => set(f.key, { ...ex, [it.code]: e.target.checked ? 1 : 0 })} aria-label={shared(`extras.${it.code}.name`)} />
                      ) : (
                        <Stepper value={n} min={0} max={it.max} onChange={(v) => set(f.key, { ...ex, [it.code]: v })} small />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : null,
      )}
    </div>
  );
}

function Stepper({ value, min, max, onChange, small }: { value: number; min: number; max: number; onChange: (n: number) => void; small?: boolean }) {
  return (
    <span className={`bo-stepper${small ? ' bo-stepper--sm' : ''}`}>
      <button type="button" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} aria-label="−">
        −
      </button>
      <b className="bo-num">{value}</b>
      <button type="button" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} aria-label="+">
        +
      </button>
    </span>
  );
}

function ClientPicker({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const { t } = useT();
  const trpc = useTRPC();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);
  const list = useQuery({ ...trpc.bo.clients.list.queryOptions({ search: debounced || undefined, sort: 'new', page: 0 }), enabled: debounced.length >= 2 && !value, placeholderData: keepPreviousData });
  const picked = useQuery({ ...trpc.bo.clients.get.queryOptions({ id: value }), enabled: !!value });
  if (value && picked.data)
    return (
      <div className="bo-person">
        <Avatar name={picked.data.name} id={picked.data.id} size={36} />
        <span className="bo-grow">
          <b>{picked.data.name}</b>
          <span className="bo-small bo-muted" style={{ display: 'block' }}>
            {[picked.data.username && `@${picked.data.username}`, picked.data.phone].filter(Boolean).join(' · ')}
          </span>
        </span>
        <button type="button" className="bo-iconbtn" onClick={() => onChange('')} aria-label={t('no.changeClient')}>
          <I name="close" size={18} />
        </button>
      </div>
    );
  return (
    <Field label={t('no.client')}>
      <input className="bo-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('no.clientPh')} />
      {debounced.length >= 2 && (
        <ul className="bo-picklist">
          {(list.data?.items ?? []).slice(0, 8).map((c) => (
            <li key={c.id}>
              <button type="button" onClick={() => onChange(c.id)}>
                <Avatar name={c.name} id={c.id} size={28} />
                <span className="bo-grow">
                  {c.name}
                  <span className="bo-faint bo-small"> {[c.username && `@${c.username}`, c.phone].filter(Boolean).join(' · ')}</span>
                </span>
                <PlanBadge plan={c.sub?.plan ?? null} t={t} />
              </button>
            </li>
          ))}
          {list.data && list.data.items.length === 0 && <li className="bo-hint" style={{ padding: 8 }}>{t('no.noClients')}</li>}
        </ul>
      )}
    </Field>
  );
}
